import { Dayjs } from 'dayjs'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { RosterRow, Shift } from '../../types/schedule.model'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import EmployeeColorPicker from '../EmployeeColorPicker/EmployeeColorPicker'
import { tileBackground } from '../../utils/colors'
import {
  dayjs,
  formatMinutes,
  toDateKey,
  formatDuration,
  totalHours
} from '../../utils/schedule'
import styles from './RosterGrid.module.scss'

interface RosterGridProps {
  days: Dayjs[]
  rows: RosterRow[]
  onAddShift: (userId: string, date: string) => void
  onEditShift: (shift: Shift) => void
  employeeColor: (userId: string) => string
  isLoading?: boolean
}

/** Placeholder rows while the roster loads, enough to fill a typical card. */
const SKELETON_ROWS = 5

/**
 * Which day cells carry a placeholder block. Fixed rather than random so the
 * skeleton doesn't reshuffle on every render while data is in flight.
 */
const SKELETON_PATTERN = [
  [0, 1, 3, 4],
  [1, 2, 5],
  [0, 2, 3, 6],
  [2, 3, 4],
  [0, 1, 4, 5, 6]
]

const isToday = (day: Dayjs) => day.isSame(dayjs(), 'day')

function RosterGrid(props: RosterGridProps) {
  const { days, rows, onAddShift, onEditShift, employeeColor, isLoading } =
    props
  const { t, i18n } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  // Same markup as the loaded grid, so nothing shifts when the data lands.
  if (isLoading) {
    return (
      <div className={styles.rosterWrapper}>
        <div className={styles.rosterScroller}>
          <div className={styles.roster}>
            <div className={styles.cornerCell} />
            {days.map(day => (
              <div key={toDateKey(day)} className={styles.dayHeader}>
                <span className="body-bold-medium">
                  {day.locale(i18n.language).format('ddd')}
                </span>
                <span className={`body-small ${styles.dayHeaderDate}`}>
                  {day.format('D.MM')}
                </span>
              </div>
            ))}

            {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => [
              <div
                key={`skeleton-name-${rowIndex}`}
                className={styles.nameCell}
              >
                <Skeleton variant="rounded" width="100%" height="2.5rem" />
              </div>,
              ...days.map((day, dayIndex) => (
                <div
                  key={`skeleton-${rowIndex}-${toDateKey(day)}`}
                  className={styles.dayCell}
                >
                  {SKELETON_PATTERN[
                    rowIndex % SKELETON_PATTERN.length
                  ].includes(dayIndex) && (
                    <Skeleton variant="rounded" width="100%" height="1.5rem" />
                  )}
                </div>
              ))
            ])}
          </div>
        </div>
      </div>
    )
  }

  if (!rows.length) {
    return (
      <p className={`body-medium ${styles.emptyState}`}>
        {t('SCHEDULE.NO_EMPLOYEES')}
      </p>
    )
  }

  return (
    <div className={styles.rosterWrapper}>
      <OverlayScrollbar targetRef={scrollerRef} />
      <div className={styles.rosterScroller} ref={scrollerRef}>
        <div className={styles.roster}>
          <div className={`${styles.cornerCell} body-bold-medium`}>
            {t('SCHEDULE.EMPLOYEE')}
          </div>

          {days.map(day => (
            <div
              key={toDateKey(day)}
              className={`${styles.dayHeader} ${isToday(day) ? styles.today : ''}`}
            >
              <span className="body-bold-medium">
                {day.locale(i18n.language).format('ddd')}
              </span>
              <span className={`body-small ${styles.dayHeaderDate}`}>
                {day.format('D.MM')}
              </span>
            </div>
          ))}

          {rows.map(row => {
            const weekShifts = days.flatMap(
              day => row.shiftsByDate[toDateKey(day)] ?? []
            )

            return [
              <div key={`${row.userId}-name`} className={styles.nameCell}>
                <EmployeeColorPicker
                  userId={row.userId}
                  color={employeeColor(row.userId)}
                  name={row.name}
                  hoursLabel={formatDuration(totalHours(weekShifts), t)}
                />
              </div>,

              ...days.map(day => {
                const dateKey = toDateKey(day)
                const shifts = row.shiftsByDate[dateKey] ?? []

                return (
                  <div
                    key={`${row.userId}-${dateKey}`}
                    className={`${styles.dayCell} ${isToday(day) ? styles.today : ''}`}
                  >
                    {shifts.map(shift => (
                      <button
                        type="button"
                        key={shift.id}
                        className={`body-small ${styles.shiftBlock}`}
                        onClick={() => onEditShift(shift)}
                        title={shift.note}
                        style={{
                          borderLeftColor: employeeColor(row.userId),
                          background: tileBackground(employeeColor(row.userId))
                        }}
                      >
                        {`${formatMinutes(shift.startMinutes)}–${formatMinutes(
                          shift.endMinutes
                        )}`}
                      </button>
                    ))}

                    <button
                      type="button"
                      className={styles.addShiftButton}
                      onClick={() => onAddShift(row.userId, dateKey)}
                      aria-label={t('SCHEDULE.ADD_SHIFT')}
                    >
                      <i className="las la-plus icon-small" />
                    </button>
                  </div>
                )
              })
            ]
          })}
        </div>
      </div>
    </div>
  )
}

export default RosterGrid
